import {
  type LeadGenCaptureProps,
  type LeadGenResponseProps,
  leadGenCapture
} from '@ecomm/data-leads'
import {
  useCurrentPromo,
  useLeadCaptureEmailSubmitted
} from '@ecomm/promotions-hooks'
import {
  COOKIE_LEAD_DATA,
  getLeadData,
  useCookieChange
} from '@ecomm/shared-cookies'
import { ReferrerUrlContext } from '@ecomm/tracking/TrackingProvider/useReferrerUrl'
import { setBrazeAttributeGoogleClientId } from '@ecomm/tracking/braze'
import { useOptimizelyTrackSiteEvents } from '@ecomm/tracking/optimizely'
import { trackLeadCapture } from '@ecomm/tracking/trackLeadCapture'
import { useGoogleAnalytics } from '@ecomm/tracking/useGoogleAnalytics'
import { useLeadGenCaptureV2 } from '@ecomm/tracking/useLeadGenCaptureV2'
import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import * as R from 'fp-ts/lib/Record'
import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'
import { useContext, useState } from 'react'
import { useTracking } from 'react-tracking'

import { useLocale } from '@ecomm/data-hooks'
import { logError } from '@ecomm/error-handling'
import type { LeadGenForm } from '@ecomm/footer-data'
import { useIdentify } from '@ecomm/shared-ninetailed'
import { useBusiness } from '@ecomm/smb-components/BusinessProvider/BusinessProvider'
import { handleLeadCaptureV2Failure } from '@ecomm/tracking/useLeadGenCaptureV2/leadGenCaptureV2'
import { getDeviceType } from '@simplisafe/ss-ecomm-data/utils/windowScreenSize'
import { toOffersData } from './helpers'

export function useOfferForm(formData: LeadGenForm) {
  const { trackEvent } = useTracking()
  const [clientId] = useGoogleAnalytics()
  const locale = useLocale()
  const optimizelyTrackSiteEvents = useOptimizelyTrackSiteEvents()
  const { promoCode, promoCodeWithMonitoring } = useCurrentPromo()
  const { isBusiness } = useBusiness()

  const [isSuccess, setIsSuccess] = useState(false)
  const [defaultEmail, setDefaultEmail] = useState<string>(
    pipe(
      getLeadData(),
      O.fromNullable,
      O.chain(R.lookup('email')),
      O.getOrElse(() => '')
    )
  )

  const { setIsSubmitted } = useLeadCaptureEmailSubmitted()

  const channel = useContext(ReferrerUrlContext).channel

  const identify = useIdentify()
  const leadGenCaptureV2 = useLeadGenCaptureV2()

  // Get device type for business flow indicators
  const deviceType = getDeviceType().toLowerCase()

  // Determine source and sourceDetails for business flows
  const getBusinessSourceDetails = () =>
    isBusiness ? `business_footer_${deviceType}` : undefined

  const handleSuccess = (value: LeadGenResponseProps) => {
    setIsSuccess(true)
    trackLeadCapture(value, trackEvent, channel)
      .finally(() => {
        const businessSourceDetails = getBusinessSourceDetails()
        pipe(
          leadGenCaptureV2({
            source: isBusiness ? 'business_flow' : 'footer',
            sourceDetail: businessSourceDetails,
            siteMetadata: { sourceType: 'footer' },
            email: value.email
          }),
          TE.mapError(handleLeadCaptureV2Failure)
        )()
      })
      .catch(logError)
    identify({ ...value, channel: channel })

    // set google client id for UK as custom braze attribute ECP-12023
    locale === 'en-GB' && clientId && setBrazeAttributeGoogleClientId(clientId)

    console.info(
      `In function useOfferForm called trackLeadCapture with ${JSON.stringify({
        leadId: value.leadId,
        externalId: value.externalId
      })}`
    )
    // When a lead is captured from any source, hide PromoBanner lead form ECP-10763
    setIsSubmitted(true)
  }

  const handleError = () => {
    setIsSuccess(false)
    optimizelyTrackSiteEvents({ eventType: 'website_error' })
  }

  const handleEmailSubmit = (email: string) => {
    const businessSourceDetails = getBusinessSourceDetails()
    const leadGenParams: LeadGenCaptureProps = {
      email,
      promoCode: promoCodeWithMonitoring || promoCode || 'NO_CODE',
      source: isBusiness ? 'business_flow' : 'footer',
      sourceType: 'footer',
      ...(businessSourceDetails && { leadSource: businessSourceDetails })
    }

    trackEvent({ event: 'submit' })
    leadGenCapture(leadGenParams)()
      .then(res => {
        pipe(
          res,
          E.match(_ => {
            handleError()
            return {}
          }, handleSuccess)
        )
      })
      .catch(() => {
        handleError()
      })
  }

  useCookieChange(COOKIE_LEAD_DATA, data => {
    setDefaultEmail(
      pipe(
        JSON.parse(data),
        // @ts-expect-error - TODO: ECP-12322 this type is unknown and the data needs to be parsed
        R.lookup('email'),
        O.getOrElse(() => '')
      )
    )
  })

  const offers = toOffersData(
    formData,
    handleEmailSubmit,
    isSuccess,
    defaultEmail
  )

  return { offers }
}
